import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { CmContentComponent } from "@claas/coremedia-components";
import { contentDetailStore } from "../../data/content-detail.store";
import { ContentStubDto } from "../../data/coremedia-content.client";
import { ContentSkeletonComponent } from "../../ui/content-skeleton/content-skeleton";

/**
 * Renders a single content element.
 * Lazy-loads the full element via IntersectionObserver when it scrolls into the viewport.
 * Each instance owns its own contentDetailStore (provided locally).
 *
 * The loaded element is passed straight to the library's `<cmc-content>`
 * dispatcher — no mapping. The library owns the CoreMedia payload contract,
 * including CAAS URL resolution (see CAAS_BASE_URL in app.config.ts).
 */
@Component({
  selector: "claas-content-item",
  providers: [contentDetailStore],
  imports: [ContentSkeletonComponent, CmContentComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <article
      class="cm-teaser"
      [class]="stub().viewType ? 'cm-teaser--' + stub().viewType : null"
      [attr.data-item-id]="stub().id"
      [attr.data-item-type]="stub().type"
    >
      @if (showSkeleton()) {
        <claas-content-skeleton class="cm-teaser__loading" />
      } @else if (store.element(); as el) {
        <cmc-content [content]="el" />
      }
    </article>
  `,
})
export class ContentItemComponent implements OnInit, OnDestroy {
  readonly stub = input.required<ContentStubDto>();

  protected readonly store = inject(contentDetailStore);

  /**
   * `idle` counts as pending: it is the state before the observer has fired, so
   * skipping the skeleton there would give the item zero height at first paint,
   * collapse the list, and pull every item into the observer's rootMargin at once.
   * `error` deliberately falls through to nothing — the failure is already surfaced
   * by httpErrorInterceptor, and a skeleton that never resolves would be a lie.
   */
  protected readonly showSkeleton = computed(
    () => this.store.status() === "idle" || this.store.status() === "loading",
  );

  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private observer: IntersectionObserver | null = null;

  ngOnInit(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          const { id, viewType, rootName } = this.stub();
          this.store.loadElement({ id, viewType: viewType ?? "", rootName });
          this.observer?.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    this.observer.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
